import React, {useCallback} from 'react';
import {useNavigate} from 'react-router';
import {useSetMergeSuggestionQueue} from 'services/mergeSuggestions';
import {
  removeMergeSuggestionFromQueue,
  selectMergeSuggestionQueue,
  selectNextMergeSuggestionInQueue,
} from 'services/mergeSuggestions/mergeSuggestionQueueSlice';
import {
  useGetMergeSuggestionBySourceIdAndTargetIdQuery,
  useMarkMergeSuggestionAsProcessedMutation,
} from 'services/mergeSuggestions/mergeSuggestionsApi';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import {FlexBox, NextButton} from 'components';
import {ActionButtonV2} from 'components/button/ActionButtonV2';
import {ConfirmButtonV2} from '../../../components/button/ConfirmButtonV2';
import {useCollapseContext} from '../context/CollapseContext';
import {useCompareEntitiesContext} from '../context/CompareEntitiesContext';

export const CompareEntitiesActions = () => {
  const navigate = useNavigate();
  const {onSave, target, source, showFullSource, toggleShowFullSource} =
    useCompareEntitiesContext();
  const {
    enabled: allowCollapseMainEntity,
    isCollapsed,
    toggleCollapse,
  } = useCollapseContext();
  const queue = useAppSelector(selectMergeSuggestionQueue);
  const setQueue = useSetMergeSuggestionQueue();
  const dispatch = useAppDispatch();

  const {data: currentMergeSuggestion} =
    useGetMergeSuggestionBySourceIdAndTargetIdQuery(
      {
        targetId: (target?.data?.id as string | undefined) ?? '',
        sourceId: (source?.data?.id as string | undefined) ?? '',
      },
      {
        // Only run this query if we have valid and existing series IDs
        skip: !target?.data?.id || !source?.data?.id,
      },
    );

  const nextMergeSuggestionInQueue = useAppSelector(
    selectNextMergeSuggestionInQueue,
  );
  const [markMergeSuggestionAsProcessedMutation] =
    useMarkMergeSuggestionAsProcessedMutation();

  const markCurrentMergeSuggestionAsProcessed = useCallback(() => {
    if (currentMergeSuggestion) {
      markMergeSuggestionAsProcessedMutation(currentMergeSuggestion.id);
    }
  }, [currentMergeSuggestion, markMergeSuggestionAsProcessedMutation]);

  const closeSuggestionAndContinue = useCallback(() => {
    markCurrentMergeSuggestionAsProcessed();
    if (nextMergeSuggestionInQueue) {
      dispatch(removeMergeSuggestionFromQueue(nextMergeSuggestionInQueue));
      navigate(nextMergeSuggestionInQueue.link);
    } else {
      navigate('/mergeOverview');
    }
  }, [
    dispatch,
    markCurrentMergeSuggestionAsProcessed,
    navigate,
    nextMergeSuggestionInQueue,
  ]);

  return (
    <FlexBox horizontal spaceBetween width={'100%'}>
      <FlexBox horizontal left width={'100%'} gap={2}>
        {allowCollapseMainEntity ? (
          <ActionButtonV2
            size={'medium'}
            color={'secondary'}
            title={`${isCollapsed ? 'Skjul' : 'Vis'} detaljer`}
            onClick={() => {
              toggleCollapse();
            }}
          />
        ) : null}

        {/* Only show the toggle source button if the main entity details are shown, to avoid confusion */}
        {isCollapsed ? (
          <ActionButtonV2
            size={'medium'}
            color={'secondary'}
            title={`${showFullSource ? 'Vis kun endringer' : 'Vis hele kilden'}`}
            onClick={() => {
              toggleShowFullSource();
            }}
          />
        ) : null}
      </FlexBox>
      <FlexBox horizontal right width={'100%'} gap={2}>
        <NextButton items={queue} setter={setQueue} />

        <ActionButtonV2
          title={'Avslå forslag'}
          color={'error'}
          onClick={() => {
            closeSuggestionAndContinue();
          }}
          /*
          If someone manually adds IDs to the url, then there might not exist a
          suggestion. But we still allow merging on these IDs, but disables
          this button.
       */
          disabled={!currentMergeSuggestion}
        />
        <ConfirmButtonV2
          icon={'Check'}
          color={'success'}
          title={'Slå sammen'}
          onClick={() => {
            onSave().then(() => {
              closeSuggestionAndContinue();
            });
          }}
        />
      </FlexBox>
    </FlexBox>
  );
};
